import { useState } from "react";
import DropdownBox from "../Common/DropdownBox";
import { Box } from "@mui/system";
import { Popover, Tooltip, Typography } from "@mui/material";
import DefaultFlag from "../../assets/action-items/default-flag.svg";

interface PrioritySelectorProps {
    value: string | null;
    onChange: (value: string) => void;
    disabled?: boolean;
    error?: boolean;
    options: Array<{ label: string; value: string; icon: string }>;
    variant?: "default" | "icon";
    tooltipTitle?: string;
    id: string;
    activeDropdownId: string | null;
    setActiveDropdownId: (id: string | null) => void;
  }
  
  const PrioritySelector: React.FC<PrioritySelectorProps> = ({
    value,
    onChange,
    disabled,
    error,
    options,
    variant = "default",
    tooltipTitle,
    id,
    activeDropdownId,
    setActiveDropdownId,
  }) => {
    const isOpen = activeDropdownId === id;
    const [localAnchorEl, setLocalAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;
      setLocalAnchorEl(e.currentTarget);
      setActiveDropdownId(id);
    };

    const handleClose = () => {
      setActiveDropdownId(null);
      setLocalAnchorEl(null);
    };
  
    const selectedOption = options.find(
      (opt) =>
        opt.label.toLowerCase() === (value ?? "").toLowerCase() ||
        opt.value?.toLowerCase() === (value ?? "").toLowerCase()
    );
  
    if (variant === "icon") {
      // icon + label block
      const chipContent = (
        <>
          <img
            src={selectedOption?.icon ?? DefaultFlag}
            alt="priority"
            style={{ width: 16, height: 16 }}
          />
  
          {selectedOption?.label && (
            <Typography
              component="span"
              sx={{ fontSize: "12px", color: "#33333B", lineHeight: 1, paddingLeft: "5px" }}
            >
              {selectedOption.label}
            </Typography>
          )}
        </>
      );
  
      // tooltip wrapper
      const wrappedWithTooltip =
        tooltipTitle ? (
          <Tooltip title={tooltipTitle} arrow placement="top">
            <span style={{ display: "inline-flex", alignItems: "center" }}>{chipContent}</span>
          </Tooltip>
        ) : (
          chipContent
        );
  
      return (
        <>
          <Box
            onClick={handleOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {wrappedWithTooltip}
          </Box>
  
          <Popover
            open={isOpen && Boolean(localAnchorEl)}
            anchorEl={localAnchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box className="dropdown-popover">
              {options.map((item, idx) => (
                <Box
                  key={idx}
                  className="dropdown-option"
                  onClick={() => {
                    onChange(item.value);
                    setLocalAnchorEl(null);
                  }}
                >
                  <img src={item.icon} alt={item.label} className="priority-flag" />
                  <Typography component="span" className="dropdown-text">
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>
        </>
      );
    }
    return (
      <>
        <DropdownBox
          onClick={handleOpen}
          error={error}
          disabled={disabled}
        >
          {selectedOption ? (
            <Box className="dropdown-content">
              <img src={selectedOption.icon} alt={selectedOption.label} className="priority-flag" />
              <Typography component="span" className="dropdown-text">
                {selectedOption.label}
              </Typography>
            </Box>
          ) : (
            <Typography component="span" className="dropdown-placeholder">
              Select
            </Typography>
          )}
        </DropdownBox>
  
        <Popover
          open={isOpen && Boolean(localAnchorEl)}
          anchorEl={localAnchorEl}
          onClose={() => setLocalAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box className="dropdown-popover">
            {options.map((item, idx) => (
              <Box
                key={idx}
                className="dropdown-option"
                onClick={() => {
                  onChange(item.value);
                  setLocalAnchorEl(null);
                }}
              >
                <img src={item.icon} alt={item.label} className="priority-flag" />
                <Typography component="span" className="dropdown-text">
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Popover>
      </>
    );
  };

  export default PrioritySelector